import { useEffect, useMemo, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import { Selection } from "./useChatData";
import Avatar from "./Avatar";
import { addActivityItem } from "./activity";
import { fetchVisibleUsers, emailForUid } from "./directoryScope";
import { parseMeta } from "./status";
import * as sounds from "./sounds";

type Props = {
  onClose: () => void;
  onSelect: (s: Selection) => void;
  onChannelCreated: (group: CometChat.Group, kind: "channel" | "group") => void;
};

/**
 * Turns a display name into a CometChat guid. ALWAYS appended with a
 * short unique suffix — without one, two channels with the same name (or
 * simply retrying after a dropped response) collide on the same guid and
 * CometChat rejects the create with ERR_GUID_ALREADY_EXISTS, which read to
 * the user as "Could not create channel" while an earlier, successful
 * attempt with that same name sat invisible in the background.
 */
function uniqueGuid(name: string, fallbackPrefix: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || fallbackPrefix;
  return `${slug}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
}

/** CometChat errors are plain objects (CometChatException), not
 * `instanceof Error` — reading `.message` directly, instead of gating it
 * behind an Error check, is what surfaces the real reason (e.g. "guid
 * already exists") instead of a generic, useless fallback every time. */
function errorMessage(e: unknown, fallback: string): string {
  const msg = (e as { message?: string } | null)?.message;
  return typeof msg === "string" && msg ? msg : fallback;
}

type ChannelVisibility = "public" | "private";

/** "+" flow: message any registered user directly, create a channel
 * (public — anyone can browse & join, or private — invite-only, pick
 * initial members right here), or join an existing public channel. */
export default function NewChatModal({ onClose, onSelect, onChannelCreated }: Props) {
  const [users, setUsers] = useState<CometChat.User[]>([]);
  const [loading, setLoading] = useState(true);
  const [channelName, setChannelName] = useState("");
  const [visibility, setVisibility] = useState<ChannelVisibility>("public");
  const [invitees, setInvitees] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();

  const [groupName, setGroupName] = useState("");
  const [groupInvitees, setGroupInvitees] = useState<Set<string>>(new Set());
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupError, setGroupError] = useState("");

  // Type-to-filter for the invite lists — scrolling through the whole
  // directory to find one name doesn't scale past a handful of people.
  const [inviteFilter, setInviteFilter] = useState("");
  const [groupInviteFilter, setGroupInviteFilter] = useState("");

  const [directory, setDirectory] = useState<CometChat.Group[] | null>(null);
  const [joiningGuid, setJoiningGuid] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchVisibleUsers(meUid)
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch((e) => console.warn("new-chat user list failed", e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [meUid]);

  useEffect(() => {
    let cancelled = false;
    const req = new CometChat.GroupsRequestBuilder().setLimit(50).joinedOnly(false).build();
    req
      .fetchNext()
      .then((list) => {
        if (cancelled) return;
        const publicNotJoined = (list as CometChat.Group[]).filter(
          (g) => g.getType() === CometChat.GROUP_TYPE.PUBLIC && !g.getHasJoined?.()
        );
        setDirectory(publicNotJoined);
      })
      .catch((e) => {
        console.warn("channel directory load failed", e);
        if (!cancelled) setDirectory([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filterUsers = (list: CometChat.User[], q: string) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(
      (u) =>
        (u.getName() || "").toLowerCase().includes(needle) ||
        (emailForUid(u.getUid()) || parseMeta(u).dbmEmail || "").toLowerCase().includes(needle)
    );
  };
  const filteredUsers = useMemo(() => filterUsers(users, inviteFilter), [users, inviteFilter]);
  const filteredGroupUsers = useMemo(
    () => filterUsers(users, groupInviteFilter),
    [users, groupInviteFilter]
  );

  const toggleInvitee = (uid: string) => {
    setInvitees((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const toggleGroupInvitee = (uid: string) => {
    setGroupInvitees((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const createChannel = async () => {
    const name = channelName.trim();
    if (!name || creating) return;
    sounds.playOnce(sounds.SOUND.click);
    setCreating(true);
    setError("");
    try {
      const guid = uniqueGuid(name, "channel");
      const type = visibility === "private" ? CometChat.GROUP_TYPE.PRIVATE : CometChat.GROUP_TYPE.PUBLIC;
      const group = new CometChat.Group(guid, name, type);
      const created = await CometChat.createGroup(group);

      if (visibility === "private" && invitees.size > 0) {
        const members = Array.from(invitees).map(
          (uid) => new CometChat.GroupMember(uid, CometChat.GROUP_MEMBER_SCOPE.PARTICIPANT)
        );
        await CometChat.addMembersToGroup(created.getGuid(), members, []).catch((e) =>
          console.warn("initial invite failed", e)
        );
      }

      onChannelCreated(created, "channel");
      addActivityItem({
        kind: "channel-created",
        text: `#${created.getName()} was created (${visibility})`,
        conversation: { kind: "group", id: created.getGuid(), name: created.getName() },
      });
      onSelect({
        kind: "group",
        id: created.getGuid(),
        name: created.getName(),
        subtitle: visibility === "private" ? "Private channel" : "Channel",
        membersCount: created.getMembersCount?.(),
        isStructuredChannel: true,
      });
      onClose();
    } catch (e) {
      console.warn("create channel failed", e);
      setError(errorMessage(e, "Could not create channel"));
    } finally {
      setCreating(false);
    }
  };

  const joinChannel = async (g: CometChat.Group) => {
    sounds.playOnce(sounds.SOUND.click);
    setJoiningGuid(g.getGuid());
    try {
      const joined = await CometChat.joinGroup(g.getGuid(), CometChat.GROUP_TYPE.PUBLIC);
      onChannelCreated(joined, "channel");
      onSelect({
        kind: "group",
        id: joined.getGuid(),
        name: joined.getName(),
        subtitle: "Channel",
        membersCount: joined.getMembersCount?.(),
        isStructuredChannel: true,
      });
      onClose();
    } catch (e) {
      console.warn("join channel failed", e);
      setJoiningGuid(null);
    }
  };

  /** Groups: casual, calling-enabled, no directory — tagged dbmKind:"group"
   * in metadata so the sidebar/header can tell them apart from Channels. */
  const createGroup = async () => {
    const name = groupName.trim();
    if (!name || creatingGroup) return;
    sounds.playOnce(sounds.SOUND.click);
    setCreatingGroup(true);
    setGroupError("");
    try {
      const guid = uniqueGuid(name, "group");
      const group = new CometChat.Group(guid, name, CometChat.GROUP_TYPE.PRIVATE);
      (group as any).setMetadata({ dbmKind: "group" });
      const created = await CometChat.createGroup(group);

      if (groupInvitees.size > 0) {
        const members = Array.from(groupInvitees).map(
          (uid) => new CometChat.GroupMember(uid, CometChat.GROUP_MEMBER_SCOPE.PARTICIPANT)
        );
        await CometChat.addMembersToGroup(created.getGuid(), members, []).catch((e) =>
          console.warn("initial invite failed", e)
        );
      }

      onChannelCreated(created, "group");
      addActivityItem({
        kind: "channel-created",
        text: `${created.getName()} group was created`,
        conversation: { kind: "group", id: created.getGuid(), name: created.getName() },
      });
      onSelect({
        kind: "group",
        id: created.getGuid(),
        name: created.getName(),
        subtitle: "Group",
        membersCount: created.getMembersCount?.(),
        isStructuredChannel: false,
      });
      onClose();
    } catch (e) {
      console.warn("create group failed", e);
      setGroupError(errorMessage(e, "Could not create group"));
    } finally {
      setCreatingGroup(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <b>New</b>
          <button className="hbtn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-section">
          <div className="modal-label">Create a channel</div>
          <div className="modal-row">
            <input
              type="text"
              placeholder="Channel name"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && void createChannel()}
            />
          </div>

          <div className="status-picker" style={{ marginTop: 10 }}>
            <button
              className={`status-picker__opt ${visibility === "public" ? "on" : ""}`}
              onClick={() => setVisibility("public")}
            >
              Public — anyone can find &amp; join
            </button>
            <button
              className={`status-picker__opt ${visibility === "private" ? "on" : ""}`}
              onClick={() => setVisibility("private")}
            >
              Private — invite only
            </button>
          </div>

          {visibility === "private" && (
            <>
              <div className="modal-label" style={{ marginTop: 12 }}>
                Invite members ({invitees.size} selected)
              </div>
              <div className="modal-row">
                <input
                  type="text"
                  placeholder="Search people…"
                  value={inviteFilter}
                  onChange={(e) => setInviteFilter(e.target.value)}
                />
              </div>
              <div className="modal-list" style={{ maxHeight: 160 }}>
                {loading && <div className="sect-empty">Loading…</div>}
                {!loading && users.length === 0 && <div className="sect-empty">No other users yet</div>}
                {!loading && users.length > 0 && filteredUsers.length === 0 && (
                  <div className="sect-empty">No matches</div>
                )}
                {filteredUsers.map((u) => (
                  <button
                    key={u.getUid()}
                    className="modal-user"
                    onClick={() => toggleInvitee(u.getUid())}
                  >
                    <input type="checkbox" checked={invitees.has(u.getUid())} readOnly />
                    <Avatar uid={u.getUid()} name={u.getName()} size="dav" />
                    <span className="nm">{u.getName()}</span>
                  </button>
                ))}
              </div>
            </>
          )}

          <button
            className="modal-btn"
            style={{ marginTop: 10 }}
            disabled={!channelName.trim() || creating}
            onClick={() => void createChannel()}
          >
            {creating ? "…" : `Create ${visibility} channel`}
          </button>
          {error && <div className="modal-error">{error}</div>}
        </div>

        <div className="modal-section">
          <div className="modal-label">Create a group</div>
          <p className="sect-empty" style={{ padding: "0 0 10px" }}>
            Quick chat + calling with a few people — no directory, invite only.
          </p>
          <div className="modal-row">
            <input
              type="text"
              placeholder="Group name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && void createGroup()}
            />
          </div>

          <div className="modal-label" style={{ marginTop: 12 }}>
            Members ({groupInvitees.size} selected)
          </div>
          <div className="modal-row">
            <input
              type="text"
              placeholder="Search people…"
              value={groupInviteFilter}
              onChange={(e) => setGroupInviteFilter(e.target.value)}
            />
          </div>
          <div className="modal-list" style={{ maxHeight: 160 }}>
            {loading && <div className="sect-empty">Loading…</div>}
            {!loading && users.length === 0 && <div className="sect-empty">No other users yet</div>}
            {!loading && users.length > 0 && filteredGroupUsers.length === 0 && (
              <div className="sect-empty">No matches</div>
            )}
            {filteredGroupUsers.map((u) => (
              <button key={u.getUid()} className="modal-user" onClick={() => toggleGroupInvitee(u.getUid())}>
                <input type="checkbox" checked={groupInvitees.has(u.getUid())} readOnly />
                <Avatar uid={u.getUid()} name={u.getName()} size="dav" />
                <span className="nm">{u.getName()}</span>
              </button>
            ))}
          </div>

          <button
            className="modal-btn"
            style={{ marginTop: 10 }}
            disabled={!groupName.trim() || creatingGroup}
            onClick={() => void createGroup()}
          >
            {creatingGroup ? "…" : "Create group"}
          </button>
          {groupError && <div className="modal-error">{groupError}</div>}
        </div>

        {!!directory?.length && (
          <div className="modal-section">
            <div className="modal-label">Browse public channels</div>
            <div className="modal-list">
              {directory.map((g) => (
                <div className="modal-user" key={g.getGuid()}>
                  <span className="hash">#</span>
                  <span className="nm">{g.getName()}</span>
                  <button
                    className="modal-btn"
                    style={{ marginLeft: "auto" }}
                    disabled={joiningGuid === g.getGuid()}
                    onClick={() => void joinChannel(g)}
                  >
                    {joiningGuid === g.getGuid() ? "…" : "Join"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
